import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEur } from "@/lib/api";
import { CheckCircle2, AlertCircle, Upload, FileSpreadsheet, Loader2 } from "lucide-react";

export default function Audit() {
  const [data, setData] = useState(null);
  const [henrri, setHenrri] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data: d } = await api.get("/audit/full");
    setData(d);
  };
  useEffect(() => { load(); }, []);

  const uploadHenrri = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: h } = await api.post("/audit/henrri", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHenrri(h);
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  if (!data) return <div className="text-sm text-stone-500" data-testid="audit-loading">Chargement…</div>;

  const c = data.contacts.by_type;
  const okContacts = Object.values(c).every((x) => x.missing === 0);
  const p = data.projects;

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Fiabilité des données</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Audit de cohérence</h1>
        <p className="text-sm text-stone-500 mt-1">Contrôle des imports vs base de données.</p>
      </div>

      {/* ===== CONTACTS ===== */}
      <section data-testid="audit-contacts">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-display text-xl font-semibold">Contacts</h2>
          {okContacts ? <span className="pill bg-emerald-50 text-emerald-700"><CheckCircle2 size={11} className="inline mr-1 -mt-0.5"/>Intègre</span>
                      : <span className="pill bg-amber-50 text-amber-700"><AlertCircle size={11} className="inline mr-1 -mt-0.5"/>Manquants</span>}
        </div>
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500 border-b">
              <tr><th className="px-4 py-3 text-left">Catégorie</th><th className="px-4 py-3 text-right">Source</th><th className="px-4 py-3 text-right">Importés</th><th className="px-4 py-3 text-right">Manquants</th><th className="px-4 py-3 text-right"></th></tr>
            </thead>
            <tbody>
              {[["Clients", "client", "/clients"], ["Fournisseurs", "fournisseur", "/fournisseurs"], ["Prestataires", "prestataire", "/prestataires"], ["Partenaires", "partenaire", "/partenaires"]].map(([label, k, to]) => (
                <tr key={k} className="border-b border-stone-100">
                  <td className="px-4 py-3 font-semibold"><Link to={to} className="hover:underline">{label}</Link></td>
                  <td className="px-4 py-3 text-right font-mono">{c[k].source}</td>
                  <td className="px-4 py-3 text-right font-mono">{c[k].imported}</td>
                  <td className={`px-4 py-3 text-right font-mono ${c[k].missing > 0 ? "text-[var(--ppd)]" : "text-emerald-700"}`}>{c[k].missing}</td>
                  <td className="px-4 py-3 text-right">{c[k].missing === 0 ? "✓" : "⚠"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-stone-50 text-xs">
              <tr><td className="px-4 py-3 font-bold">Total</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{data.contacts.total_source}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{data.contacts.total_current}</td>
                  <td className="px-4 py-3 text-right"></td><td/></tr>
            </tfoot>
          </table>
        </div>
        <div className="text-xs text-stone-500 mt-2">{data.contacts.multi_type} contact(s) cumulent plusieurs types (ex: fournisseur + prestataire).</div>
      </section>

      {/* ===== PROJETS ===== */}
      <section data-testid="audit-projects">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-display text-xl font-semibold">Projets</h2>
          <span className="text-sm text-stone-500">{p.total} projets en base</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total" value={p.total} to="/projets"/>
          {Object.entries(p.par_status).map(([k, v]) => <Stat key={k} label={k} value={v} to={`/projets?tab=${k === "livre" ? "livres" : k === "archive" ? "archives" : k}`}/>)}
        </div>
      </section>

      {/* ===== HENRRI / FINANCE ===== */}
      <section data-testid="audit-henrri">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="font-display text-xl font-semibold">Henrri & Finance</h2>
          <label className="ml-auto bg-stone-900 hover:bg-black text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-2 cursor-pointer">
            {loading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {loading ? "Analyse…" : "Téléverser journal Henrri (XLSX)"}
            <input type="file" accept=".xlsx" onChange={uploadHenrri} className="hidden" data-testid="audit-henrri-file"/>
          </label>
        </div>
        {err && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded mb-3 px-3 py-2">{err}</div>}

        {!henrri ? (
          <div className="card-flat p-5 text-sm text-stone-500">
            Téléversez votre journal Henrri pour comparer le CA source ↔ le CA enregistré dans l'ERP.
            <div className="mt-2 text-xs">État ERP actuel : <span className="font-mono">{data.henrri_db_only.db.finance_docs} doc(s)</span> · CA facturé <span className="font-mono">{formatEur(data.henrri_db_only.db.ca_facture)}</span></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="CA brut Henrri" value={formatEur(henrri.henrri.ca_brut_ttc)}/>
              <Stat label="Avoirs Henrri" value={formatEur(henrri.henrri.ca_avoirs_ttc)} accent="text-stone-500"/>
              <Stat label="CA net Henrri" value={formatEur(henrri.henrri.ca_net_ttc)}/>
              <Stat label="CA facturé ERP" value={formatEur(henrri.db.ca_facture)} accent={henrri.reconciliation.ca_ecart_ttc !== 0 ? "text-[var(--ppd)]" : "text-emerald-700"}/>
            </div>

            <div className={`card-flat p-4 ${Math.abs(henrri.reconciliation.ca_ecart_ttc) > 100 ? "border-[var(--ppd)]/40" : ""}`} data-testid="audit-reconciliation">
              <h3 className="font-display font-semibold text-lg mb-2">Réconciliation</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <Row label="Écart CA facturé (TTC)" value={formatEur(henrri.reconciliation.ca_ecart_ttc)} alert={Math.abs(henrri.reconciliation.ca_ecart_ttc) > 100}/>
                <Row label="Écart CA encaissé (TTC)" value={formatEur(henrri.reconciliation.ca_encaisse_ecart_ttc)} alert={Math.abs(henrri.reconciliation.ca_encaisse_ecart_ttc) > 100}/>
                <Row label="N° documents manquants" value={henrri.reconciliation.numeros_manquants_count} alert={henrri.reconciliation.numeros_manquants_count > 0}/>
                <Row label="N° inattendus dans l'ERP" value={henrri.reconciliation.numeros_inattendus.length} alert={henrri.reconciliation.numeros_inattendus.length > 0}/>
              </div>
              {henrri.reconciliation.numeros_manquants_count > 0 && (
                <div className="mt-3 text-xs text-stone-600">
                  Échantillon des manquants : <span className="font-mono">{henrri.reconciliation.numeros_manquants_sample.slice(0, 10).join(", ")}{henrri.reconciliation.numeros_manquants_count > 10 ? "…" : ""}</span>
                </div>
              )}
            </div>

            <div className="card-flat p-4">
              <h3 className="font-display font-semibold text-lg mb-2">Projets à créer / valider</h3>
              <div className="text-sm">
                <span className="font-mono font-bold text-stone-900">{henrri.henrri.raison_sociales_unique}</span> raisons sociales uniques dans Henrri, dont
                <span className="font-mono font-bold text-emerald-700"> {henrri.henrri.raison_sociales_matched_projet}</span> rattachée(s) à un projet existant et
                <span className="font-mono font-bold text-[var(--ppd)]"> {henrri.henrri.raison_sociales_a_creer.length}</span> à créer.
              </div>
              {henrri.henrri.raison_sociales_a_creer.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {henrri.henrri.raison_sociales_a_creer.map((rs) => (
                    <span key={rs} className="pill bg-[var(--pink-powder)]/40 text-stone-700 text-[10px]">{rs}</span>
                  ))}
                </div>
              )}
              <Link to="/finance" className="mt-4 inline-flex bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold items-center gap-2 no-underline">
                <FileSpreadsheet size={14}/> Lancer l'import complet depuis Finance
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, accent, to }) {
  const Wrap = to ? Link : "div";
  return (
    <Wrap to={to} className={`card-flat p-3 ${to ? "hover:border-[var(--ppd)]/40" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{label}</div>
      <div className={`font-mono text-xl font-bold mt-1 ${accent || "text-stone-900"}`}>{value}</div>
    </Wrap>
  );
}

function Row({ label, value, alert }) {
  return (
    <div className={`flex justify-between items-center p-2 rounded ${alert ? "bg-[var(--pink-powder)]/40" : "bg-stone-50"}`}>
      <span className="text-stone-600">{label}</span>
      <span className={`font-mono font-bold ${alert ? "text-[var(--ppd)]" : "text-emerald-700"}`}>{value}</span>
    </div>
  );
}
